// FUNCTIONALITY-105 — in-WebView diagnostic panel (staging only).
// Proves route/build/catalog/counts/consent/localStorage/checkout inside Telegram itself.
import { useEffect, useMemo, useState } from "react";
import { getCatalogDiagnostics } from "./catalog-v2-data.js";

const CHECKOUT_ENDPOINT = "/api/store-v2/checkout";
const EVENT_ENDPOINT = "/api/store-v2/event";
const STORE_V2_LS_PREFIX = "asiastyle.store-v2.";

function readStoreV2Keys(): string[] {
  const keys: string[] = [];
  try {
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const k = window.localStorage.key(i);
      if (k && k.startsWith(STORE_V2_LS_PREFIX)) keys.push(k);
    }
  } catch {
    /* storage unavailable */
  }
  return keys;
}

export interface DiagnosticsV2Props {
  buildId: string;
  consentAccepted: boolean;
  onResetCache: () => void;
  onClose: () => void;
}

export function DiagnosticsV2({ buildId, consentAccepted, onResetCache, onClose }: DiagnosticsV2Props) {
  const [checkoutStatus, setCheckoutStatus] = useState<string>("перевірка…");
  const [copied, setCopied] = useState(false);
  const diag = getCatalogDiagnostics();
  const lsKeys = readStoreV2Keys();

  const route = typeof window !== "undefined" ? window.location.pathname : "";
  const url = typeof window !== "undefined" ? window.location.href : "";

  useEffect(() => {
    let cancelled = false;
    // Bounded, best-effort reachability probe (OPTIONS — no order created).
    fetch(CHECKOUT_ENDPOINT, { method: "OPTIONS", cache: "no-store" })
      .then((r) => {
        if (!cancelled) setCheckoutStatus(`HTTP ${r.status}`);
      })
      .catch(() => {
        if (!cancelled) setCheckoutStatus("недоступний");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const report = useMemo(() => {
    const lines = [
      `build_id: ${buildId}`,
      `route: ${route}`,
      `url: ${url}`,
      `catalog_url_used: ${diag.catalogUrlUsed ?? "—"}`,
      `catalog_url_tried: ${diag.catalogUrlTried.join(" | ") || "—"}`,
      `catalog_http: ${diag.catalogHttp ?? "—"}`,
      `meta_url_used: ${diag.metaUrlUsed ?? "—"}`,
      `product_count: ${diag.productCount}`,
      `category_count: ${diag.categoryCount}`,
      `consent_accepted: ${consentAccepted ? "yes" : "no"}`,
      `localStorage_store_v2: ${lsKeys.join(", ") || "(empty)"}`,
      `checkout_endpoint: ${CHECKOUT_ENDPOINT}`,
      `checkout_status: ${checkoutStatus}`,
      `event_endpoint: ${EVENT_ENDPOINT}`,
      `nav_route_errors: ${(() => {
        try {
          return String(
            (window as Window & { __AS_CUSTOMER_NAV_DIAG__?: { route_errors?: number } }).__AS_CUSTOMER_NAV_DIAG__
              ?.route_errors ?? 0,
          );
        } catch {
          return "0";
        }
      })()}`,
      `last_error: ${diag.lastError ?? "—"}`,
      `user_agent: ${typeof navigator !== "undefined" ? navigator.userAgent : "—"}`,
    ];
    return lines.join("\n");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildId, route, url, checkoutStatus, consentAccepted, lsKeys.length]);

  const copy = () => {
    try {
      void navigator.clipboard?.writeText(report);
    } catch {
      /* clipboard may be blocked in WebView — textarea below is the fallback */
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  const Row = ({ k, v, ok }: { k: string; v: string; ok?: boolean }) => (
    <div className="sv2-diag-row">
      <span className="sv2-diag-k">{k}</span>
      <span className={`sv2-diag-v${ok === false ? " sv2-diag-bad" : ok === true ? " sv2-diag-ok" : ""}`}>{v}</span>
    </div>
  );

  return (
    <div className="sv2-diag-overlay" role="dialog" aria-modal="true" aria-label="Діагностика">
      <div className="sv2-diag-card">
        <div className="sv2-diag-head">
          <strong>🩺 Діагностика Store V2</strong>
          <button type="button" className="sv2-navbtn" onClick={onClose}>✕</button>
        </div>

        <Row k="Build" v={buildId} ok={buildId.includes("105")} />
        <Row k="Маршрут" v={route} ok={route.includes("s2-105")} />
        <Row k="Каталог HTTP" v={String(diag.catalogHttp ?? "—")} ok={diag.catalogHttp === 200} />
        <Row k="Каталог URL" v={diag.catalogUrlUsed ?? "—"} />
        <Row k="Товарів" v={String(diag.productCount)} ok={diag.productCount > 3000} />
        <Row k="Категорій" v={String(diag.categoryCount)} ok={diag.categoryCount === 17} />
        <Row k="Згода" v={consentAccepted ? "так" : "ні"} ok={consentAccepted} />
        <Row k="Checkout" v={checkoutStatus} ok={checkoutStatus.includes("204") || checkoutStatus.includes("200")} />
        <Row k="localStorage" v={lsKeys.join(", ") || "(порожньо)"} />
        <Row k="Остання помилка" v={diag.lastError ?? "—"} ok={diag.lastError == null} />

        <textarea className="sv2-diag-text" readOnly value={report} rows={8} />

        <div className="sv2-diag-actions">
          <button type="button" className="sv2-cta" onClick={copy}>
            {copied ? "✓ Скопійовано" : "Скопіювати діагностику"}
          </button>
          <button type="button" className="sv2-cta sv2-secondary" onClick={onResetCache}>
            Скинути кеш магазину
          </button>
        </div>
      </div>
    </div>
  );
}
